import { useMemo } from 'react'
import { BuyerProposalDetailDto } from '../../shared/types'
import { BidTableRow } from './bids-table'
import { SelectedBidTableRow } from './selected-bids-table'
import { calculateSOTR } from './bid-utils'
import { useIssuers } from '../issuers-context'

export const useFlattenedBids = (
  proposal: BuyerProposalDetailDto | null,
  scenarioNamesMap: { [scenarioId: string]: string }
): {
  flattenedBids: BidTableRow[]
  flattenedSelectedBids: SelectedBidTableRow[]
  isLoading: boolean
} => {
  const { issuers, isLoading: issuersLoading } = useIssuers()

  const { flattenedBids, flattenedSelectedBids } = useMemo(() => {
    if (!proposal?.scenarios) {
      return { flattenedBids: [], flattenedSelectedBids: [] }
    }

    const bidsResult: BidTableRow[] = []
    const selectedBidsResult: SelectedBidTableRow[] = []

    proposal.scenarios.forEach((scenario) => {
      // Get scenario name from the mapping (shared by both)
      const scenarioName = scenarioNamesMap[scenario.id] || 'Unknown Scenario'

      // Process regular bids
      scenario.bids.forEach((bid) => {
        // Find issuer name from issuers context
        const issuer = issuers.find(
          (i) => i.issuerTenantId === bid.issuerTenantId
        )
        const issuerName = issuer?.displayName || 'Loading...'

        // Transform bid to table row format
        const tableRow: BidTableRow = {
          bidId: bid.id,
          scenarioId: scenario.id, // Include scenario ID for reliable lookups
          issuerTenantId: bid.issuerTenantId, // Include issuer tenant ID for reliable lookups
          scenarioName,
          issuerName,
          rate: bid.rate,
          treasuryRate: bid.treasuryRate,
          sotr: calculateSOTR(bid.rate, bid.treasuryRate),
          issuerComments: bid.notes,
          notes: bid.notes,
          bidTime: bid.createdAt,
          bidStatus: bid.status,
        }

        bidsResult.push(tableRow)
      })

      // Process selected bids
      scenario.selectedBids.forEach((selectedBid) => {
        // Find issuer name from issuers context
        const issuer = issuers.find(
          (i) => i.issuerTenantId === selectedBid.issuerTenantId
        )
        const issuerName = issuer?.displayName || 'Loading...'

        // Use selected bid's own wamYears/partialMaturities if available, fall back to scenario
        const wamYears = selectedBid.wamYears ?? scenario.wamYears ?? null
        const partialMaturities =
          selectedBid.partialMaturities &&
          selectedBid.partialMaturities.length > 0
            ? selectedBid.partialMaturities
            : scenario.paymentSchedule.map((p) => ({
                paymentDate: p.paymentDate,
                amount: p.payoutPercentage,
                inputType: 'PERCENTAGE',
              }))

        // Transform selected bid to table row format with precomputed values
        const tableRow: SelectedBidTableRow = {
          ...selectedBid,
          issuerName,
          scenarioName,
          sotr: calculateSOTR(selectedBid.rate, selectedBid.treasuryRate),
          wamYears,
          partialMaturities,
        }

        selectedBidsResult.push(tableRow)
      })
    })

    return {
      flattenedBids: bidsResult,
      flattenedSelectedBids: selectedBidsResult,
    }
  }, [proposal?.scenarios, scenarioNamesMap, issuers])

  return {
    flattenedBids,
    flattenedSelectedBids,
    isLoading: issuersLoading,
  }
}
